import type { ReplicaResponsePart } from '@unni-craftsman/turso-fts-common/dist/replication'
import { refusalReasonBytes, validateRefusals, type RefusalCode } from './refusal-code'

// Version + digest; page tag + maximum JS byte-array length varint; two signed-63-bit cursors.
const responseHeaderBytes = 36;
const pageFramingBytes = 6;
const pageHeaderBytes = 20;
// IncomingPage field 6 `through` (S4): tag + maximal signed-63-bit cursor, reserved only when the page states it.
const throughFieldBytes = 10;
export const replicaResponseOverheadBytes = responseHeaderBytes + pageFramingBytes + pageHeaderBytes;

// Vendored ROW-SCOPE-01 server envelope codec (Turso fork, MIT). IncomingPage field 5 carries own receipts (requirements.md:100).
export class ReplicaRequestError extends Error {
    readonly status: 400 | 403 | 413;
    override readonly cause?: unknown;
    constructor(status: 400 | 403 | 413, message: string, cause?: unknown) { super(message); this.status = status; this.cause = cause; }
}

export interface ReplicaAppend {
    replica: Uint8Array;
    sequence: bigint;
    cursor: bigint;
    bytes: Uint8Array;
    budget?: { digest: Uint8Array; rows: number; originalBytes: number };
}

export interface ReplicaReceipt {
    sequence: bigint;
    revision: bigint;
    digest: Uint8Array;
    refused?: { index: number; reason: RefusalCode }[];
}

export interface ReplicaCatalogAppend extends ReplicaAppend {
    base: { version: bigint; digest: Uint8Array };
    result: { version: bigint; digest: Uint8Array };
}

/** `through` (S4): the last revision the page covers; revisions in (since, through] it does not list are not the replica's. */
export type ReplicaPage = {
    since: bigint; head: bigint; through?: bigint; transactions: { revision: bigint; bytes: Uint8Array }[];
    skipped?: { revision: bigint; bytes: Uint8Array }[];
    own?: ReplicaReceipt[];
};

export interface ReplicaLogStore {
    append(input: ReplicaAppend): Promise<ReplicaReceipt>;
    appendCatalog?(input: ReplicaCatalogAppend): Promise<ReplicaReceipt>;
    exchange?(input: ReplicaAppend): Promise<{ receipt: ReplicaReceipt; page: ReplicaPage }>;
    exchangeVector?(input: ReplicaAppend[]): Promise<{ receipts: ReplicaReceipt[]; page: ReplicaPage }>;
    readStream?(input: { replica: Uint8Array; cursor: bigint }): Promise<ReplicaPage>;
    read(input: { replica: Uint8Array; cursor: bigint }): Promise<ReplicaPage>;

}

export function createReplicaRemote(store: ReplicaLogStore, limits: { requestBytes: number; responseBytes: number }) {
    const { requestBytes, responseBytes } = limits;
    if (![requestBytes, responseBytes].every(value => Number.isSafeInteger(value) && value > 0)) {
        throw new RangeError("Replica byte limits must be positive safe integers");
    }
    function validatePage(page: ReplicaPage, cursor: bigint) {
        const bound = page.through ?? page.head;
        if (page.since !== cursor || page.head < cursor || page.head > 0x7fffffffffffffffn || bound < cursor || bound > page.head) throw new Error("Storage page has an invalid cursor");
        const ordered = [
            ...page.transactions.map(transaction => ({ revision: transaction.revision, bytes: transaction.bytes, skipped: false, receipt: undefined })),
            ...(page.skipped ?? []).map(transaction => ({ revision: transaction.revision, bytes: transaction.bytes, skipped: true, receipt: undefined })),
            ...(page.own ?? []).map(receipt => ({ revision: receipt.revision, bytes: receipt.digest, skipped: false, receipt })),
        ].toSorted((a,b) => a.revision < b.revision ? -1 : a.revision > b.revision ? 1 : 0);
        for (const list of [page.transactions, page.skipped ?? []]) {
            for (let i = 1; i < list.length; i++) if (list[i].revision <= list[i - 1].revision) throw new Error("Storage page has an invalid transaction order");
        }
        let previous = cursor;
        for (const transaction of ordered) {
            if (!positiveRevision(transaction.revision) || transaction.revision <= previous || transaction.revision > bound || transaction.bytes.length === 0) throw new Error("Storage page has an invalid transaction");
            if (transaction.receipt && (transaction.receipt.sequence <= 0n || transaction.receipt.digest.length !== 32)) throw new Error("Storage page has an invalid own receipt");
            previous = transaction.revision;
        }
        if (page.head > cursor && ordered.length === 0 && (page.through ?? cursor) === cursor) throw new Error("Storage page made no progress");
        return { ordered, previous };
    }
    async function readPage(replica: Uint8Array, cursor: bigint, captured?: ReplicaPage, limit = responseBytes): Promise<Uint8Array> {
            const page = captured ?? await store.read({ replica, cursor });
            const { ordered, previous } = validatePage(page, cursor);
            const orderedParts = ordered.map(transaction => transaction.receipt
                ? blob(5, concat([integer(1, transaction.receipt.sequence), blob(2, transaction.receipt.digest), integer(3, transaction.receipt.revision)]))
                : blob(transaction.skipped ? 4 : 3, concat([integer(1, transaction.revision), blob(2, transaction.bytes)])));
            const parts = orderedParts;
            // Two maximal signed-63-bit cursor fields, plus `through` when it passes the last listed revision.
            const through = page.through ?? previous;
            const statesThrough = through > previous;
            let used = pageHeaderBytes + (statesThrough ? throughFieldBytes : 0);
            const selected: Uint8Array[] = [];
            for (const part of parts) { if (used + part.length > limit) break; selected.push(part); used += part.length; }
            if (selected.length === parts.length) return concat([integer(1, page.since), integer(2, page.head), ...selected.toSorted((a, b) => a[0] - b[0]), ...(statesThrough ? [integer(6, through)] : [])]);
            // The device refuses a page that does not advance its cursor.
            if (selected.length === 0) throw new Error("Storage page entry exceeds the response budget");
            // A bounded prefix keeps head and states no through: the device stops at its last listed revision and pulls the
            // omitted entries next, toward the head it validates every later page against.
            return concat([integer(1, page.since), integer(2, page.head), ...selected.toSorted((a, b) => a[0] - b[0])]);
    }
    return {
        async sendRemote(bytes: Uint8Array): Promise<Uint8Array> {
            const request = validateRequest(bytes, requestBytes);
            if (request.transactions.length === 0) throw new ReplicaRequestError(400, "Send requires a transaction vector");
            const frames = request.transactions.map(transaction => validateTransaction(transaction, request.replica, Boolean(request.budget)));
            if (frames.length > 1 && (request.transactions.some(requiresSingletonTransaction) || frames.some((frame, index) => frame.sequence !== frames[0].sequence + BigInt(index)))) throw new ReplicaRequestError(400, "Vector must be contiguous ordinary transactions; catalog and custom DDL require a singleton request");
            if (frames.some(frame => frame.catalog) && !store.appendCatalog) throw new ReplicaRequestError(403, "Catalog publication is not authorized");
            if (frames.length > 1 && !store.exchangeVector) throw new ReplicaRequestError(400, "Storage requires a singleton exchange");
            const receiptBytes = frames.reduce((total, frame) => total + worstCaseReceiptBytes(frame.sequence, request.budget?.rows ?? frame.rows), 0);
            // Version + request digest + maximum page header/framing; no storage call yet.
            if (replicaResponseOverheadBytes + receiptBytes > responseBytes) throw new ReplicaRequestError(400, 'Vector worst-case receipts exceed response byte limit');
            const requestDigest = await hash(request.bytes);
            const inputs = await Promise.all(request.transactions.map(async (transaction, index) => ({ replica: request.replica, cursor: request.cursor, sequence: frames[index].sequence, bytes: transaction,
                budget: request.budget ?? { digest: await hash(transaction), rows: frames[index].rows, originalBytes: transaction.length } })));
            const frame = frames[0];
            const input = inputs[0];
            const vector = frames.length > 1 && store.exchangeVector ? await store.exchangeVector(inputs) : undefined;
            const exchanged = !vector && !frame.catalog && store.exchange ? await store.exchange(input) : undefined;
            const receipts = vector?.receipts ?? [exchanged?.receipt ?? (frame.catalog && store.appendCatalog
                ? await store.appendCatalog({ ...input, ...frame.catalog }) : await store.append(input))];
            if (receipts.length !== frames.length) throw new Error("Storage omitted vector receipts");
            const encoded = receipts.map((receipt, index) => {
            const frame = frames[index];
            const digest = inputs[index].budget.digest;
            const refused = receipt.refused ?? [];
            const rowCount = request.budget?.rows ?? frame.rows;
            if (receipt.sequence !== frame.sequence || !equal(receipt.digest, digest) || (refused.length > 0 ? receipt.revision !== 0n : !positiveRevision(receipt.revision))) {
                throw new Error("Storage receipt differs from the transaction");
            }
            // Whole-transaction refusal: revision 0, one reason per row in row order.
            if (refused.length > 0 && (frame.catalog || refused.length !== rowCount || refused.some((row, index) => row.index !== index || !row.reason))) throw new Error("Invalid row refusal receipt");
            if (refused.length > 0) validateRefusals(refused, rowCount);
            return blob(3, concat([
                integer(1, receipt.sequence), blob(2, digest), integer(3, receipt.revision),
                ...refused.map(row => blob(4, concat([integer(1, BigInt(row.index)), blob(2, new TextEncoder().encode(row.reason))]))),
            ]));
            });
            const result = concat([integer(1, 1n), blob(2, requestDigest), ...encoded, blob(4, await readPage(request.replica, request.cursor, vector?.page ?? exchanged?.page, responseBytes - responseHeaderBytes - encoded.reduce((total, receipt) => total + receipt.length, 0) - pageFramingBytes))]);
            if (result.length > responseBytes) throw new RangeError("Replica response exceeds byte limit");
            return result;
        },
        async *fetchStreamRemote(bytes: Uint8Array): AsyncGenerator<ReplicaResponsePart> {
            const request = validateRequest(bytes, requestBytes);
            if (request.transactions.length > 0) throw new ReplicaRequestError(400, "Fetch must not contain a transaction");
            const digest = await hash(request.bytes);
            let cursor = request.cursor;
            let target: bigint | undefined;
            do {
                const page = await (store.readStream ? store.readStream({ replica: request.replica, cursor }) : store.read({ replica: request.replica, cursor }));
                const { ordered: transactions } = validatePage(page, cursor);
                if (page.own?.length) throw new Error("Stream pages require transaction frames, not receipts");
                if (target === undefined) {
                    target = page.head;
                    yield { type: 'Begin', bytes: response(digest, 4, concat([integer(1, cursor), integer(2, target), integer(6, target)]), responseBytes) };
                }
                for (const transaction of transactions) {
                    if (transaction.revision > target) break;
                    yield { type: 'Transaction', revision: transaction.revision, bytes: transaction.bytes };
                }
                const through = page.through ?? transactions.at(-1)?.revision ?? cursor;
                const next = through > target ? target : through;
                if (next <= cursor && cursor < target) throw new Error('Storage page made no progress');
                cursor = next;
                yield { type: 'PageEnd', cursor };
            } while (cursor < target);
            yield { type: 'Complete', cursor };
        },
        async fetchRemote(bytes: Uint8Array): Promise<Uint8Array> {
            const request = validateRequest(bytes, requestBytes);
            if (request.transactions.length > 0) throw new ReplicaRequestError(400, "Fetch must not contain a transaction");
            const requestDigest = await hash(request.bytes);
            return response(requestDigest, 4, await readPage(request.replica, request.cursor, undefined, responseBytes - responseHeaderBytes - pageFramingBytes), responseBytes);
        },
    };
}

function validateRequest(bytes: Uint8Array, limit: number) {
    if (bytes instanceof Uint8Array && bytes.length > limit) throw new ReplicaRequestError(400, "Oversize pushes require a metadata envelope");
    try { return decodeRequest(bytes, limit); }
    catch (error) { throw new ReplicaRequestError(400, error instanceof Error ? error.message : "Invalid replica request", error); }
}

function validateTransaction(bytes: Uint8Array, replica: Uint8Array, metadata = false) {
    try { return decodeTransaction(bytes, replica, metadata); }
    catch (error) { throw new ReplicaRequestError(400, error instanceof Error ? error.message : "Invalid replica transaction", error); }
}

function decodeRequest(input: Uint8Array, limit: number) {
    if (!(input instanceof Uint8Array)) throw new TypeError("Replica request must be bytes");
    if (input.length > limit) throw new RangeError("Replica request exceeds byte limit");
    const bytes = new Uint8Array(input);
    let version = 0n;
    let replica = new Uint8Array();
    let cursor = 0n;
    const transactions: Uint8Array[] = [];
    let metadata: Uint8Array | undefined;
    for (const [tag, value] of fields(bytes, [0, 2, 0, 2, 2], [4])) {
        if (tag === 1) version = value as bigint;
        else if (tag === 2) replica = value as Uint8Array<ArrayBuffer>;
        else if (tag === 3) cursor = value as bigint;
        else if (tag === 4) transactions.push(value as Uint8Array);
        else metadata = value as Uint8Array;
    }
    if (version !== 1n || replica.length !== 16 || cursor > 0x7fffffffffffffffn) throw new Error("Invalid replica request identity or version");
    let budget: ReplicaAppend['budget'];
    if (metadata) {
        let digest = new Uint8Array(); let rows = 0; let originalBytes = 0;
        for (const [tag, value] of fields(metadata, [2, 0, 0])) {
            if (tag === 1) digest = value as Uint8Array<ArrayBuffer>;
            else if (tag === 2) rows = Number(value);
            else originalBytes = Number(value);
        }
        if (transactions.length !== 1 || digest.length !== 32 || !Number.isSafeInteger(rows) || rows <= 0 || rows > 0xffffffff || !Number.isSafeInteger(originalBytes) || originalRequestBytes(replica,cursor,originalBytes) <= limit) throw new Error("Invalid oversize transaction metadata");
        budget = { digest, rows, originalBytes };
    }
    if (transactions.length > 512) throw new Error("Send vector exceeds 512 transactions");
    return { bytes, replica, cursor, transactions, budget };
}

/** Same barrier as the client's frozen-prefix classifier: catalog kind or custom DDL carrier. */
export function requiresSingletonTransaction(bytes: Uint8Array): boolean {
    for (const [tag, value] of fields(bytes, [0, 2, 0, 2, 2, 0, 0, 2, 2, 0], [5, 8])) {
        if (tag === 7 && value !== 0n) return true;
        if (tag === 5 && value instanceof Uint8Array) {
            for (const [rowTag, rowValue] of fields(value, [0, 2, 0, 2, 2, 2, 2])) {
                if (rowTag === 2 && rowValue instanceof Uint8Array) {
                    if (new TextDecoder().decode(rowValue).toLowerCase() === '__cdb_ddl_log') return true;
                    break;
                }
            }
        }
        // Catalog frames carry ordered schema/row events, never an ordinary row vector.
        if (tag === 8) return true;
    }
    return false;
}

function decodeTransaction(bytes: Uint8Array, requestReplica: Uint8Array, metadata = false) {
    let version = 0n;
    let replica = new Uint8Array();
    let sequence = 0n;
    let schema = new Uint8Array();
    let databaseVersion = 0n;
    let kind = 0n;
    let events = 0;
    let baseSchema = new Uint8Array();
    let baseVersion = 0n;
    let rows = 0;
    for (const [tag, value] of fields(bytes, [0, 2, 0, 2, 2, 0, 0, 2, 2, 0], [5, 8])) {
        if (tag === 1) version = value as bigint;
        else if (tag === 2) replica = value as Uint8Array<ArrayBuffer>;
        else if (tag === 3) sequence = value as bigint;
        else if (tag === 4) schema = value as Uint8Array<ArrayBuffer>;
        else if (tag === 5) rows++;
        else if (tag === 6) databaseVersion = value as bigint;
        else if (tag === 7) kind = value as bigint;
        else if (tag === 8) events++;
        else if (tag === 9) baseSchema = value as Uint8Array<ArrayBuffer>;
        else baseVersion = value as bigint;
    }
    if (version !== 1n || !equal(replica, requestReplica) || !positiveRevision(sequence) || schema.length !== 32 || databaseVersion > 0x7fffffffffffffffn) {
        throw new Error("Invalid transaction identity or schema");
    }
    if (kind === 0n) {
        if ((metadata ? rows !== 0 : rows === 0) || events !== 0 || baseSchema.length > 0 || baseVersion !== 0n) throw new Error("Invalid data transaction fields");
        return { sequence, rows, catalog: undefined };
    }
    if (kind !== 1n || rows !== 0 || events === 0 || baseSchema.length !== 32 || databaseVersion <= baseVersion || equal(baseSchema, schema)) {
        throw new Error("Invalid catalog transaction fields");
    }
    return { sequence, rows, catalog: { base: { version: baseVersion, digest: baseSchema }, result: { version: databaseVersion, digest: schema } } };
}

function* fields(bytes: Uint8Array, types: number[], repeated: number[] = []): Generator<[number, bigint | Uint8Array]> {
    let offset = 0;
    let previous = 0;
    const read = () => {
        let result = 0n;
        for (let index = 0; index < 10; index++) {
            if (offset >= bytes.length) throw new Error("Truncated replica message");
            const byte = bytes[offset++];
            if (index === 9 && byte > 1) throw new Error("Replica integer overflow");
            result |= BigInt(byte & 127) << BigInt(index * 7);
            if (!(byte & 128)) {
                if (index > 0 && byte === 0) throw new Error("Noncanonical replica integer");
                return result;
            }
        }
        throw new Error("Replica integer overflow");
    };
    while (offset < bytes.length) {
        const key = read();
        const tag = Number(key >> 3n);
        const wire = Number(key & 7n);
        if (tag < 1 || tag > types.length || types[tag - 1] !== wire || tag < previous || (tag === previous && !repeated.includes(tag))) {
            throw new Error("Unknown, repeated or noncanonical replica field");
        }
        previous = tag;
        const value = read();
        if (wire === 0) {
            if (value === 0n) throw new Error("Noncanonical replica default");
            yield [tag, value];
        } else {
            if (value === 0n || value > BigInt(bytes.length - offset)) throw new Error("Invalid replica field length");
            const end = offset + Number(value);
            yield [tag, bytes.subarray(offset, end)];
            offset = end;
        }
    }
}

function response(digest: Uint8Array, tag: number, payload: Uint8Array, limit: number, page?: Uint8Array): Uint8Array {
    const bytes = concat([integer(1, 1n), blob(2, digest), blob(tag, payload), ...(page ? [blob(4, page)] : [])]);
    if (bytes.length > limit) throw new RangeError("Replica response exceeds byte limit");
    return bytes;
}

export function integer(tag: number, value: bigint): Uint8Array {
    return value === 0n ? new Uint8Array() : concat([varint(BigInt(tag * 8)), varint(value)]);
}

export function blob(tag: number, bytes: Uint8Array): Uint8Array {
    return concat([varint(BigInt(tag * 8 + 2)), varint(BigInt(bytes.length)), bytes]);
}

function varint(value: bigint): Uint8Array {
    if (value < 0n || value > 0xffffffffffffffffn) throw new RangeError("Invalid replica integer");
    const bytes: number[] = [];
    do {
        const byte = Number(value & 127n);
        value >>= 7n;
        bytes.push(byte | (value ? 128 : 0));
    } while (value);
    return new Uint8Array(bytes);
}

export function concat(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
    const bytes = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
    let offset = 0;
    for (const part of parts) { bytes.set(part, offset); offset += part.length; }
    return bytes;
}

async function hash(bytes: Uint8Array): Promise<Uint8Array> {
    return new Uint8Array(await crypto.subtle.digest("SHA-256", new Uint8Array(bytes)));
}

function equal(left: Uint8Array, right: Uint8Array): boolean {
    return left.length === right.length && left.every((byte, index) => byte === right[index]);
}

function positiveRevision(value: bigint): boolean {
    return value > 0n && value <= 0x7fffffffffffffffn;
}

/** Canonical data header used for an all-refused published-empty transaction. */
export function stripTransactionRows(bytes: Uint8Array): Uint8Array {
    return concat([...fields(bytes, [0,2,0,2,2,0,0,2,2,0], [5,8])].filter(([tag]) => tag !== 5).map(([tag,value]) => typeof value === 'bigint' ? integer(tag,value) : blob(tag,value)));
}

/** Transport-only compression; frozen engine bytes and receipt identity stay unchanged. */
export async function prepareBudgetPush(bytes: Uint8Array, requestBytes: number): Promise<{ bytes: Uint8Array; restore: (response: Uint8Array) => Promise<Uint8Array> }> {
    if (bytes.length <= requestBytes) return { bytes, restore: response => Promise.resolve(response) };
    const request = decodeRequest(bytes, bytes.length);
    if (request.transactions.length !== 1) throw new Error('Oversize atomic transactions require a singleton metadata envelope');
    const transaction = request.transactions[0];
    const frame = decodeTransaction(transaction, request.replica);
    if (frame.catalog) throw new Error('Catalog transaction exceeds request limit');
    const digest = await hash(transaction);
    const metadata = concat([blob(1,digest),integer(2,BigInt(frame.rows)),integer(3,BigInt(transaction.length))]);
    const envelope = concat([integer(1,1n),blob(2,request.replica),integer(3,request.cursor),blob(4,stripTransactionRows(transaction)),blob(5,metadata)]);
    const compressedDigest = await hash(envelope);
    return { bytes: envelope, async restore(responseBytes) {
        const parts = [...fields(responseBytes,[0,2,2,2])];
        const responseDigest = parts.find(([tag]) => tag === 2)?.[1];
        if (!(responseDigest instanceof Uint8Array) || !equal(responseDigest,compressedDigest)) throw new Error('Metadata response differs from request');
        const originalDigest = await hash(bytes);
        return concat(parts.map(([tag,value]) => tag === 2 ? blob(2,originalDigest) : typeof value === 'bigint' ? integer(tag,value) : blob(tag,value)));
    } };
}

export function isEmptyDataTransaction(bytes: Uint8Array): boolean {
    try {
        let replica: bigint | Uint8Array | undefined;
        for (const [tag, value] of fields(bytes,[0,2,0,2,2,0,0,2,2,0],[5,8])) {
            // Any row rules out an empty transaction, including malformed input.
            if (tag === 5) return false;
            if (tag === 2) replica = value;
        }
        if (!(replica instanceof Uint8Array)) return false;
        const frame = decodeTransaction(bytes,replica,true);
        return !frame.catalog;
    } catch { return false; }
}

function originalRequestBytes(replica: Uint8Array, cursor: bigint, transactionBytes: number): number {
    return integer(1,1n).length + blob(2,replica).length + integer(3,cursor).length + varint(34n).length + varint(BigInt(transactionBytes)).length + transactionBytes;
}

/** Receipt field framing included; sequence is frozen, revision may use nine varint bytes. */
export function worstCaseReceiptBytes(sequence: bigint, rows: number): number {
    const size = (value: number) => { let bytes = 1; while (value >= 128) { value = Math.floor(value / 128); bytes++; } return bytes; };
    // Index zero is omitted. All other indices have one tag plus their varint.
    let refusals = rows * (4 + refusalReasonBytes);
    for (let start = 1, width = 1; start < rows; start *= 128, width++) refusals += (Math.min(rows, start * 128) - start) * (1 + width);
    const header = integer(1, sequence).length + 34;
    const framed = (payload: number) => 1 + size(payload) + payload;
    // Acceptance may also appear in the own-only page; refusal has no revision field.
    return Math.max(framed(header + refusals), 2 * framed(header + 10));
}

export type WireField = { tag: number; wire: number; value: bigint | Uint8Array; raw: Uint8Array }
export function wireFields(bytes: Uint8Array): WireField[] {
  const out: WireField[] = []
  let offset = 0
  const read = () => {
    let result = 0n, shift = 0n
    for (;;) {
      if (offset >= bytes.length) throw new Error('Truncated replication message')
      const byte = bytes[offset++]
      result |= BigInt(byte & 127) << shift
      shift += 7n
      if (!(byte & 128)) return result
    }
  }
  while (offset < bytes.length) {
    const start = offset
    const key = read()
    const tag = Number(key >> 3n), wire = Number(key & 7n)
    if (wire === 0) out.push({ tag, wire, value: read(), raw: bytes.subarray(start, offset) })
    else if (wire === 2) {
      const length = Number(read())
      out.push({ tag, wire, value: bytes.subarray(offset, offset + length), raw: bytes.subarray(start, offset + length) })
      offset += length
    } else throw new Error('Unsupported replication wire type')
  }
  return out
}
